<template>
  <div class="mydepartment bg-white report pb-12">
    <div class="text-white font-bold p-0 bg-primary-default">
      <div class="ml-auto w-11/12">
        <div class="grid grid-rows md:grid-cols md:grid-cols-3">
          <div class="py-4">
            <!-- Back Link -->
            <div class="text-sm mb-1 cursor-pointer -ml-1">
              <router-link to="/">
                <ChevronLeftIcon class="h-5 w-5 inline-block align-text-top" aria-hidden="true" />
                <span class="align-middle">Patiënt zoeken</span>
              </router-link>
            </div>
            <h1 class="text-2xl font-bold tracking-tight text-white m-0 pl-1">Mijn afdeling</h1>
          </div>
          <div class="justify-items-end place-content-center space-x-2 mr-9 md:mr-12 col-span-2">
            <div
              class="flex justify-end place-content-end items-start md:items-end self-start flex-wrap flex-row mb-4 lg:mb-0 gap-y-2"
            >
              <button
                @click="$router.push('/collega-registreren')"
                class="flex justify-center py-2 px-2 md:px-4 border border-white shadow-sm text-sm font-medium text-white rounded-full bg-transpartent hover:bg-primary-default/50 items-center mr-0 md:mr-4"
              >
                <PlusCircleIcon class="h-5 w-5 flex-shrink-0 text-white" />
                <span class="ml-1">Collega toevoegen</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section class="pt-6 mx-auto w-10/12 min-h-screen text-pdsPrimaryColor">
      <div class="shadow bg-white rounded border broder-bdefault" v-if="myDepartmentTexts">
        <h1 class="py-4 px-6 font-bold text-xl flex text-talternative-color bg-balternative-color my-0">Gegevens</h1>
        <legend class="mx-4 mt-4">
          Instelling: <span>{{ myDepartmentTexts.institutionName }}</span>
        </legend>
        <legend class="mx-4 mt-4">
          Afdeling: <span>&nbsp;&nbsp;{{ myDepartmentTexts.departmentName }}</span>
        </legend>
        <tables class="m-4">
          <template #tableHeaders>
            <thead>
              <tr class="bg-sky-50">
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor text-balance max-w-48 xl:max-w-full min-w-32"
                  v-for="header in tableHeaders"
                  :key="header"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>
          </template>
          <template #tableBody>
            <tbody class="bg-white divide-y divide-sky-200">
              <tr v-for="(colleague, index) in departmentColleagues" :key="index">
                <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-base text-pdsPrimaryColor w-3">
                  {{ colleague.name }}
                </td>
                <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-base text-pdsPrimaryColor w-3">
                  {{ colleague.externalId }}
                </td>
                <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-base text-pdsPrimaryColor w-3">
                  {{ colleague.email }}
                </td>
                <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-base text-pdsPrimaryColor w-3">
                  <span v-show="colleague.isPrescriber">
                    <CheckCircleIcon class="h-6 w-6 flex-shrink-0 text-pdsPrimaryColor" aria-hidden="true" />
                  </span>
                  <span v-show="!colleague.isPrescriber">
                    <XCircleIcon class="h-6 w-6 flex-shrink-0 text-gray-300" aria-hidden="true" />
                  </span>
                </td>
                <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-base text-pdsPrimaryColor w-3">
                  <span v-if="colleague.warranters">{{ colleague.warranters.join(', ') }}</span>
                </td>
              </tr>
            </tbody>
          </template>
        </tables>
      </div>
      <form @submit.prevent="submitComments">
        <div class="shadow bg-white rounded border broder-bdefault mt-4" v-if="myDepartmentTexts">
          <h1 class="py-4 px-6 font-bold text-xl flex text-talternative-color bg-balternative-color my-0">
            Kloppen uw gegevens nog?
          </h1>
          <legend class="mx-4 mt-4">
            Wij horen het graag wanneer hier onjuiste gegevens staan. Na ontvangst van uw feedback zullen wij deze
            z.s.m. controleren en verwerken.
          </legend>
          <div class="mx-4 mb-8">
            <v-textarea-group v-model="comments" label="Uw bericht:" inputid="comments" @input="saveComments" />
            <div v-show="showErrorMessage" class="form-group-simple__details errorMessage">
              <div class="form-group-simple__messages">
                <div class="form-group-simple__error">* Dit veld is verplicht.</div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex col-2 py-4 justify-end gap-2">
          <button
            type="submit"
            class="text-white text-center rounded-full border w-32 h-10 cursor-pointer bg-pdsPrimaryColor border-pdsPrimaryColor"
          >
            VERSTUREN
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import { useStore, mapActions } from 'vuex';
import { departmentColleagues$, myDepartmentTexts$ } from '@/services';
import router from '@/router';
import { ref, getCurrentInstance } from 'vue';
import { PlusCircleIcon, ChevronLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid';
export default {
  name: 'Mydepartment',
  components: {
    PlusCircleIcon,
    ChevronLeftIcon,
    CheckCircleIcon,
    XCircleIcon
  },
  emits: ['success'],
  setup(props, { emit }) {
    const store = useStore();
    const submitFeedback = (value) => store.dispatch('patient/submitFeedback', value);
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const departmentColleagues = ref(null);
    const myDepartmentTexts = ref(null);
    const comments = ref('');
    const showErrorMessage = ref(false);
    const tableHeaders = ['Naam', 'Mediq referentie', 'E-mail', 'Voorschrijver', 'Onder volmacht van'];
    departmentColleagues$.subscribe((value) => (departmentColleagues.value = value));
    myDepartmentTexts$.subscribe((value) => (myDepartmentTexts.value = value));

    const saveComments = (event) => {
      comments.value = event.target ? event.target.value : '';
      showErrorMessage.value = comments.value !== '' ? false : true;
    };

    const submitComments = () => {
      if (comments.value !== '') {
        submitFeedback({ Comments: comments.value }).then((response) => {
          if (response === true) {
            emit('success');
            $emitter.emit('alert', { message: 'Bedankt, uw suggestie is met succes verstuurd!' });
          } else {
            $emitter.emit('alert', {
              message: 'Oops het versturen ging niet helemaal goed. Laten we dit als eerste maar eens verbeteren.'
            });
          }
          setTimeout(() => {
            router.push('/');
          }, 2000);
        });
      } else {
        showErrorMessage.value = true;
      }
    };

    return {
      ...mapActions('patient', ['submitFeedback']),
      submitFeedback,
      departmentColleagues,
      myDepartmentTexts,
      comments,
      showErrorMessage,
      tableHeaders,
      saveComments,
      submitComments
    };
  }
};
</script>
